import { useCallback, useEffect, useState } from "react";
import { AntennaRead, RfidFetchResponse, StartnumberRfidTag} from "../api/Dtos";
import { useGlobalContext } from "../Layout/GlobalState";
import { DELETE, GET, POST, PUT } from "../api/BackendApi";
import { toast } from "react-toastify";


export function useStartnumberRfidTagService(targetStartnumber: number | null)  { 
  const globalContext = useGlobalContext();

  // DB content
  const [allPersistedTags, setPersistedStartnumberRfidTags] = useState<Array<StartnumberRfidTag>>([]);
  const [allKnownAntennas, setAllKnownAntennas] = useState<Set<string>>(new Set());

  // Here we buffer up on tag read info until we accept and persist 
  const [detectedTags, setDetectedTags] = useState<Array<{tag: string, antennas: Set<string>}>>([]);
  const [detectedConflicts, setDetectedConflicts] = useState<Array<string>>([]);
  const [detectedAlreadyPersistedTags, setDetectedAlreadyPersistedTags] = useState<Array<string>>([]);
  const [lastFetchTime, setLastFetchTime] = useState((new Date()).getTime());

  // Fetch all tags initially when loading the context
  useEffect(() => {
    if (globalContext.competition){
          GET.allStartnumberRfidTags(globalContext.competition.id).then((response) => {
            setPersistedStartnumberRfidTags(response);
        });
    }
  }, []);


  // Set state for new startnumber
  useEffect(() => {
      setDetectedAlreadyPersistedTags([]);
      setDetectedTags([]);
      setDetectedConflicts([]);
  }, [targetStartnumber]);


  const fetchNewTagsForStartnumber = useCallback(async (tagCount: number, numberOfAntennasInUse: number, antennas: Array<string>) => {
    const detectedTagsNames = new Set(detectedTags.map(t => t.tag));
    const detectedConflictTagNames = new Set(detectedConflicts);

    const noCompetitionSelected = globalContext.competition?.id == null;
    const conflictsFound = detectedConflictTagNames.size != 0;
    const okAmountOfTagsFound = tagCount <= detectedTagsNames.size;
    const allTagsFoundByAllAntennas = detectedTags.every(t => antennas.every(a => t.antennas.has(a)));

    if (noCompetitionSelected || conflictsFound || (okAmountOfTagsFound && allTagsFoundByAllAntennas)) {
      return; // Do not fetch any new tags
    }

    const response = await GET.fetchRfidTags(numberOfAntennasInUse, antennas, lastFetchTime);

    const incommingTagNames = new Set(response.antennaReads.map(a => a.tag));

    const newDetectedTagNames = difference(incommingTagNames, detectedTagsNames);
    const repeatedlyDetectedTagNames = intersection(incommingTagNames, detectedTagsNames);

    // Figure out new conflict tags
    const newDetectedTags = allPersistedTags.filter(t => newDetectedTagNames.has(t.rfidtag));
    const newConflictTagNames = new Set(newDetectedTags
      .filter(t => t.startnumber != targetStartnumber)     // Is a conflict (as startnumbers do not match)
      .filter(t => !detectedConflicts.includes(t.rfidtag)) // Not already detected conflict
      .map(t => t.rfidtag));

    // Figure out new already-seen tags
    const newAlreadyPersistedTagNames = new Set(newDetectedTags       
      .filter(t => t.startnumber === targetStartnumber)                // Is not a conflict
      .filter(t => !detectedAlreadyPersistedTags.includes(t.rfidtag))  // Not already detected 
      .map(t => t.rfidtag));             


    const fetchAntennas = (tagName: string) => new Set(response.antennaReads.filter(ar => ar.tag === tagName).map(ar => ar.antenna));

    // Add newly detected tags
    if (newDetectedTagNames.size > 0) {
      const tmpArray = [...newDetectedTagNames];
      setDetectedTags((old) => [...old, ...tmpArray.map(tagName => ({tag: tagName, antennas: fetchAntennas(tagName)}))]);
    }

    // Update antenna detection info on already seen tags
    repeatedlyDetectedTagNames.forEach((alreadySeenTagName) => {
      const antennas = fetchAntennas(alreadySeenTagName);
      const alreadyRegisteredAntennas = new Set(detectedTags.filter(c => c.tag === alreadySeenTagName).flatMap(c => [...c.antennas]));
      const newAntennas = difference(antennas, alreadyRegisteredAntennas);
      if (newAntennas.size > 0) {
        setDetectedTags((old) => old.map((e) => e.tag === alreadySeenTagName ? {...e, antennas: union(newAntennas, e.antennas)} : e));
      }
    })

    // Add new conflicts
    if (newConflictTagNames.size > 0) {
      setDetectedConflicts(old => [...old, ...newConflictTagNames]);
    }

    // Add new already persisted tags observations
    if (newAlreadyPersistedTagNames.size > 0) {
      setDetectedAlreadyPersistedTags(old => [...old, ...newAlreadyPersistedTagNames]);
    }

    // Collect new known antennas
    const newAntennas = difference(new Set(response.antennaReads.map(e => e.antenna)), allKnownAntennas);
    if (newAntennas.size > 0) {
      setAllKnownAntennas(old => new Set([...old, ...newAntennas]))
    }

    setLastFetchTime(response.responseTimestamp);
  }, [globalContext.competition?.id, allKnownAntennas, allPersistedTags, lastFetchTime, detectedTags, detectedConflicts, detectedAlreadyPersistedTags]);

  const forgetDetectedTag = (tag: string) => {
    setDetectedTags((old) => old.filter(t => t.tag != tag));
    setDetectedConflicts((old) => old.filter(t => t != tag));
    setDetectedAlreadyPersistedTags((old) => old.filter(t => t != tag));
  }

  const postDbEntry = useCallback(async () => {
    if (targetStartnumber == null) {
      return; 
    }

    for (const t of detectedTags) { 
      await POST.startnumberRfidTag(globalContext.competition!.id, targetStartnumber, t.tag).then((response: StartnumberRfidTag) => {
        toast.success(`New rfidTag in DB: ${response.id}, ${response.startnumber}, ${response.rfidtag}`);
        setPersistedStartnumberRfidTags(old => [...old, response])
      });
    }

    detectedAlreadyPersistedTags.forEach((tag) => {
      const persisted = allPersistedTags.filter(pt => pt.rfidtag === tag);
      persisted.forEach((e) => {
        toast.info(`Verified rfidTag-startnumber mapping: ${e.id}, ${e.startnumber}, ${e.rfidtag}`);
      })
    });

    detectedConflicts.forEach((tag) => {
      const persisted = allPersistedTags.filter(pt => pt.rfidtag === tag);
      persisted.forEach((e) => {
        toast.warn(`Ignoring conflict with: ${e.id}, ${e.startnumber}, ${e.rfidtag}`);
      })
    })
  }, [targetStartnumber, detectedTags, detectedAlreadyPersistedTags, detectedConflicts, allPersistedTags]);

  const putDbEntry = useCallback(async (id: number, tagName: string, startnumber: number) => {
    PUT.startnumberRfidTag(id, tagName, startnumber).then((response: StartnumberRfidTag) => { 
        setPersistedStartnumberRfidTags((old) => old.map(e => e.id == response.id ? response: e));
        if (detectedTags.map(t => t.tag).includes(tagName)) {
          if (targetStartnumber === response.startnumber) {
            setDetectedConflicts((old) => old.filter(tag => tag != tagName));
            setDetectedAlreadyPersistedTags((old) => ([...old.filter(t => t != tagName), tagName]));
          }
          else {
            setDetectedAlreadyPersistedTags((old) => old.filter(tag => tag != tagName));
            setDetectedConflicts((old) => ([...old.filter(t => t != tagName), tagName]));
          }
        }
    });
  }, []);

  const deleteDbEntry = useCallback(async (tagName: string) => {
      DELETE.startnumberRfidTags(globalContext.competition!.id, tagName).then((response: Array<StartnumberRfidTag>) => { 
        response.forEach(r => {
          setPersistedStartnumberRfidTags((old) => old.filter(e => e.id != r.id));
        })
        setDetectedAlreadyPersistedTags((old) => old.filter(tag => tag != tagName));
        setDetectedConflicts((old) => old.filter(tag => tag != tagName));
      });
  }, []);

  return { 
    db: {
      allTags: allPersistedTags, 
      allAntennas: allKnownAntennas, 
    },
    startnumber: {
      detectedTags, 
      detectedConflicts, 
      detectedAlreadyPersistedTags,
    },
    functions: {
      fetch: fetchNewTagsForStartnumber,
      saveNewTags: postDbEntry,
      put: putDbEntry,
      delete: deleteDbEntry,
      forgetDetectedTag: forgetDetectedTag
    }
};
}

function union<T>(a: Set<T>, b: Set<T>): Set<T> {
  return new Set([...a, ...b]);
}

function intersection<T>(a: Set<T>, b: Set<T>): Set<T> {
  return new Set([...a].filter(item => b.has(item)));
}

function difference<T>(a: Set<T>, b: Set<T>): Set<T> {
  return new Set([...a].filter(item => !b.has(item)));
}
